import React from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, MessageCircle, Instagram, Linkedin } from 'lucide-react';
import { mockCompanyInfo } from '../mock';
import logo from '../images/gheika_logo.png';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <img src={logo} alt="Gheika Logo" className="h-12 w-auto mb-4" />
            <p className="text-gray-400 mb-6 leading-relaxed max-w-md">
              {t('footer.description')}
            </p>
            
            {/* Social Media Section */}
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3">{t('footer.social')}</h4>
              <div className="flex space-x-4">
                <a
                  href={mockCompanyInfo.contact.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-gray-800 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-700 transition-all duration-300 group"
                  title="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href={mockCompanyInfo.contact.socialMedia.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-gray-800 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-700 transition-all duration-300 group"
                  title="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Contact Icons */}
            <div className="flex space-x-4">
              <a
                href={`tel:3017891934`}
                className="p-3 bg-gray-800 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-700 transition-all duration-300"
                title="Llamar"
              >
                <Phone className="w-5 h-5" />
              </a>
              <a
                href={`mailto:${mockCompanyInfo.contact.email}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gray-800 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-700 transition-all duration-300"
                title="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
              <a
                href={`https://wa.me/${mockCompanyInfo.contact.whatsapp.replace(/[\s\-\(\)]/g, '')}?text=${encodeURIComponent(t('contact.whatsappMessage'))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-gray-800 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-700 transition-all duration-300"
                title="WhatsApp"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {[
                { name: t('nav.home'), href: '#inicio' },
                { name: t('nav.services'), href: '#servicios' },
                { name: t('nav.projects'), href: '#proyectos' },
                { name: t('nav.team'), href: '#equipo' },
                { name: t('nav.contact'), href: '#contacto' }
              ].map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">{t('footer.contactInfo')}</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  {mockCompanyInfo.contact.address}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  {mockCompanyInfo.contact.phone}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-red-500 flex-shrink-0" />
                <span className="text-gray-400 text-sm">
                  {mockCompanyInfo.contact.email}
                </span>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-6">
              <h5 className="text-white font-medium mb-2">{t('footer.hours.title')}</h5>
              <div className="text-gray-400 text-sm space-y-1">
                <div>{t('footer.hours.weekdays')}</div>
                <div>{t('footer.hours.saturday')}</div>
                <div>{t('footer.hours.sunday')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <button className="text-gray-400 hover:text-red-500 text-sm transition-colors duration-200">
                {t('footer.privacy')}
              </button>
              <button className="text-gray-400 hover:text-red-500 text-sm transition-colors duration-200">
                {t('footer.terms')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;